import React, { useState, useRef, useEffect } from 'react';

interface ContextMenuItem {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    color?: string;
}

interface ContextMenuProps {
    items: ContextMenuItem[];
}

const ContextMenu: React.FC<ContextMenuProps> = ({ items }) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen]);

    const handleItemClick = (item: ContextMenuItem) => {
        if (!item.disabled) {
            item.onClick();
            setIsOpen(false);
        }
    };

    return (
        <div ref={menuRef} style={{ position: 'relative' }}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '5px 10px',
                    color: '#667eea',
                    lineHeight: 1,
                }}
                aria-label="Možnosti"
            >
                ⋮
            </button>

            {isOpen && (
                <div
                    style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        minWidth: '220px',
                        marginTop: '5px',
                    }}
                >
                    {items.map((item, index) => (
                        <div
                            key={index}
                            onClick={() => handleItemClick(item)}
                            style={{
                                padding: '12px 16px',
                                cursor: item.disabled ? 'not-allowed' : 'pointer',
                                borderBottom: index < items.length - 1 ? '1px solid #f0f0f0' : 'none',
                                color: item.disabled ? '#999' : item.color || '#333',
                                fontSize: '14px',
                                transition: 'background-color 0.2s',
                                opacity: item.disabled ? 0.5 : 1,
                            }}
                            onMouseEnter={(e) => {
                                if (!item.disabled) {
                                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            {item.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ContextMenu;
